// src/components/RetentionLedger.tsx
// Phase 1 — G08 / R12
// Retention tracking: cumulative held, released, balance per subcontract.
// Retention release workflow at practical completion / DLP end.

import { useState } from 'react'
import { CheckCircle, AlertTriangle, DollarSign, Lock, Unlock } from 'lucide-react'
import { Subcontract } from '../utils/mockDataSubcontracts'
import { formatCurrency } from '../utils/formatters'

interface RetentionLedgerProps {
  subcontract: Subcontract
  canRelease: boolean
}

function fmtDate(iso?: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' })
}

export function RetentionLedger({ subcontract: sc, canRelease }: RetentionLedgerProps) {
  const [released, setReleased]     = useState(sc.retentionReleased)
  const [showRelease, setShowRelease] = useState(false)
  const [releaseNote, setReleaseNote] = useState('')
  const [releaseType, setReleaseType] = useState<'PC' | 'DLP'>('PC')

  const paid      = sc.milestones.filter(m => m.paid)
  const totalPaid = paid.reduce((s, m) => s + m.amountPayable, 0)
  const held      = paid.reduce((s, m) => s + m.retentionWithheld, 0)
  const balance   = held - released
  const pctReleased = held > 0 ? Math.round(released / held * 100) : 0

  const canReleasePC = ['PRACTICAL_COMPLETION','DEFECTS','COMPLETE'].includes(sc.status)

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Contract value',            value: formatCurrency(sc.contractValue), color: 'text-gray-900' },
          { label: `Retention held (${sc.retentionPct}%)`, value: formatCurrency(held),    color: held > 0 ? 'text-amber-700' : 'text-gray-400' },
          { label: 'Released',                  value: formatCurrency(released),          color: released > 0 ? 'text-green-700' : 'text-gray-400' },
          { label: 'Balance held',              value: formatCurrency(balance),           color: balance > 0 ? 'text-red-700' : 'text-green-700' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white border border-gray-200 rounded-xl px-4 py-3">
            <div className={`text-xl font-bold tabular-nums ${color}`}>{value}</div>
            <div className="text-xs text-gray-500 mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {/* Progress bar */}
      <div>
        <div className="flex justify-between text-xs text-gray-500 mb-1"><span>Retention status</span><span>{pctReleased}% released</span></div>
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${pctReleased}%` }} />
        </div>
        <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
          <span>No release</span><span>PC release (50%)</span><span>DLP release (100%)</span>
        </div>
      </div>

      {/* Milestone ledger */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="bg-gray-50 border-b px-4 py-2.5 text-xs font-semibold text-gray-700">Milestone retention ledger</div>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b bg-gray-50 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
              <th className="text-left px-4 py-2">Milestone</th>
              <th className="text-right px-3 py-2">Claim amount</th>
              <th className="text-right px-3 py-2">Retention ({sc.retentionPct}%)</th>
              <th className="text-right px-3 py-2">Amount payable</th>
              <th className="text-center px-3 py-2">Status</th>
              <th className="text-left px-4 py-2">Date paid</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {sc.milestones.map(m => (
              <tr key={m.id} className={`${m.paid ? 'bg-green-50/30' : ''} hover:bg-gray-50`}>
                <td className="px-4 py-2.5">
                  <div className="font-medium text-gray-900">{m.description}</div>
                  <div className="text-[10px] text-gray-400">Due {fmtDate(m.dueDate)}</div>
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">{formatCurrency(m.claimAmount)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-amber-700">{formatCurrency(m.retentionWithheld)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-gray-900">{formatCurrency(m.amountPayable)}</td>
                <td className="px-3 py-2.5 text-center">
                  {m.paid
                    ? <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Paid</span>
                    : m.completedDate
                    ? <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Awaiting payment</span>
                    : <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium">Not yet due</span>}
                </td>
                <td className="px-4 py-2.5 text-gray-500">
                  {m.paidAt ? fmtDate(m.paidAt) : '—'}
                  {m.invoiceRef && <div className="text-[10px] text-gray-400 font-mono">{m.invoiceRef}</div>}
                </td>
              </tr>
            ))}
            <tr className="bg-gray-50 border-t-2 border-gray-200 font-semibold">
              <td className="px-4 py-2 text-xs text-gray-700">Totals (paid milestones)</td>
              <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(paid.reduce((s,m) => s + m.claimAmount, 0))}</td>
              <td className="px-3 py-2 text-right tabular-nums text-amber-700">{formatCurrency(held)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-gray-900">{formatCurrency(totalPaid)}</td>
              <td /><td />
            </tr>
          </tbody>
        </table>
      </div>

      {/* Release section */}
      {canRelease && canReleasePC && balance > 0 && !showRelease && (
        <button onClick={() => setShowRelease(true)}
          className="flex items-center gap-2 text-sm bg-green-100 text-green-700 hover:bg-green-200 px-4 py-2 rounded-xl font-medium">
          <Unlock className="w-4 h-4" />Release retention ({formatCurrency(balance)})
        </button>
      )}

      {canRelease && showRelease && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-3">
          <h4 className="font-semibold text-green-900 text-sm flex items-center gap-2"><Unlock className="w-4 h-4" />Release retention</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Release type</label>
              <select className="input text-sm" value={releaseType} onChange={e => setReleaseType(e.target.value as any)}>
                <option value="PC">Practical completion (half-release)</option>
                <option value="DLP">DLP expiry (full release)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Release amount</label>
              <div className="input text-sm bg-gray-50 text-gray-700">{formatCurrency(releaseType === 'PC' ? balance / 2 : balance)}</div>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Authorisation note <span className="text-red-500">*</span></label>
            <input className="input text-sm" value={releaseNote} onChange={e => setReleaseNote(e.target.value)} placeholder="PM name and confirmation that PC / DLP criteria have been met…" />
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs text-amber-800">
            <AlertTriangle className="w-3 h-3 inline mr-1" />Retention release is irreversible. Verify the {releaseType === 'PC' ? 'practical completion' : 'defects liability period'} has been formally certified.
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowRelease(false)} className="btn-secondary text-sm">Cancel</button>
            <button onClick={() => { setReleased(held); setShowRelease(false); setReleaseNote('') }}
              disabled={releaseNote.trim().length < 10}
              className="btn-primary text-sm disabled:opacity-50 flex items-center gap-2">
              <DollarSign className="w-4 h-4" />Release {formatCurrency(releaseType === 'PC' ? balance / 2 : balance)}
            </button>
          </div>
        </div>
      )}

      {balance === 0 && (
        <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 border border-green-200 rounded-xl px-4 py-2.5">
          <CheckCircle className="w-4 h-4" />All retention released — this subcontract is fully settled
        </div>
      )}

      {!canRelease && balance > 0 && (
        <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5">
          <Lock className="w-4 h-4" />PM sign-off required to release retention ({formatCurrency(balance)} balance)
        </div>
      )}
    </div>
  )
}
